// A small arithmetic expression evaluator: + - * /, unary minus, parentheses, decimal
// literals and named variables — the grammar the Client's Rift.Sim.FormulaEvaluator
// implements, so a formula the Editor accepts is one the game evaluates (the game does
// it in Q16 fixed point; here it is plain floating point, which is what a preview needs).
//
// Deliberately not eval(): a malformed formula must come back as `undefined` — the
// "invalid formula" state — rather than throw or silently yield 0, and token
// substitution by string replace collides as soon as one variable name is a prefix of
// another (COST / SCORE / TURNS are safe, but the player score formula's GPT / PT were not).

export type FormulaVariables = Readonly<Record<string, number>>;

// The value of `formula` over `variables`, or undefined when it does not parse, names a
// variable that is not supplied, divides by zero, or is blank.
export function evaluateFormula(formula: string, variables: FormulaVariables): number | undefined {
    if (!formula || formula.trim().length === 0) return undefined;
    try {
        const parser = new Parser(formula, variables);
        const value = parser.parseExpression();
        parser.expectEnd();
        return Number.isFinite(value) ? value : undefined;
    } catch {
        return undefined;
    }
}

// True when the formula parses over the given variable names (their values do not matter).
export function isValidFormula(formula: string, variableNames: readonly string[]): boolean {
    const variables: Record<string, number> = {};
    for (const name of variableNames) variables[name] = 1;
    return evaluateFormula(formula, variables) !== undefined;
}

class Parser {
    private position = 0;

    constructor(private readonly text: string, private readonly variables: FormulaVariables) {}

    // expression := term (('+' | '-') term)*
    parseExpression(): number {
        let value = this.parseTerm();
        for (;;) {
            this.skipSpaces();
            const c = this.peek();
            if (c === '+') { this.position++; value += this.parseTerm(); }
            else if (c === '-') { this.position++; value -= this.parseTerm(); }
            else return value;
        }
    }

    expectEnd(): void {
        this.skipSpaces();
        if (this.position !== this.text.length) throw new Error(`Unexpected '${this.peek()}' at ${this.position}`);
    }

    // term := factor (('*' | '/') factor)*
    private parseTerm(): number {
        let value = this.parseFactor();
        for (;;) {
            this.skipSpaces();
            const c = this.peek();
            if (c === '*') { this.position++; value *= this.parseFactor(); }
            else if (c === '/') {
                this.position++;
                const divisor = this.parseFactor();
                if (divisor === 0) throw new Error('Division by zero');
                value /= divisor;
            }
            else return value;
        }
    }

    // factor := '-' factor | '(' expression ')' | number | identifier
    private parseFactor(): number {
        this.skipSpaces();
        const c = this.peek();
        if (c === '-') { this.position++; return -this.parseFactor(); }
        if (c === '(') {
            this.position++;
            const inner = this.parseExpression();
            this.skipSpaces();
            if (this.peek() !== ')') throw new Error(`Expected ')' at ${this.position}`);
            this.position++;
            return inner;
        }
        if (isDigit(c) || c === '.') return this.parseNumber();
        if (isLetter(c) || c === '_') return this.parseIdentifier();
        throw new Error(`Unexpected '${c === '' ? 'end' : c}' at ${this.position}`);
    }

    // Digits with an optional '.' and more digits. The decimal separator is always '.',
    // whatever the browser's locale — a formula is source text, not a localized number.
    private parseNumber(): number {
        const start = this.position;
        while (isDigit(this.peek())) this.position++;
        if (this.peek() === '.') {
            this.position++;
            while (isDigit(this.peek())) this.position++;
        }
        const literal = this.text.slice(start, this.position);
        if (literal === '.') throw new Error(`Expected a number at ${start}`);
        return Number(literal);
    }

    private parseIdentifier(): number {
        const start = this.position;
        while (isLetter(this.peek()) || isDigit(this.peek()) || this.peek() === '_') this.position++;
        const name = this.text.slice(start, this.position);
        if (!(name in this.variables)) throw new Error(`Unknown variable '${name}'`);
        return this.variables[name];
    }

    private skipSpaces(): void {
        while (this.position < this.text.length && /\s/.test(this.text[this.position])) this.position++;
    }

    private peek(): string {
        return this.position < this.text.length ? this.text[this.position] : '';
    }
}

function isDigit(c: string): boolean { return c >= '0' && c <= '9'; }
function isLetter(c: string): boolean { return (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z'); }
